import { useState, useEffect } from 'react';
import { Building2, Shield, Users, Wrench } from 'lucide-react';
import { api } from '../lib/api';
import type { Property } from '../types';
import PropertyCard from '../components/PropertyCard';
import { Spinner } from '../components/ui';
import ImageHero from '../components/ImageHero';

export default function Management() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ items: Property[] }>('/properties?listing_type=management&limit=30')
      .then((data) => setProperties(data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <ImageHero height="h-[66vh]" imageKey="hero_image_management" fallbackClass="bg-management-hero">
        <div className="max-w-7xl mx-auto px-4 h-full flex items-start pt-[33vh]">
          <div className="text-white max-w-3xl">
            <p className="uppercase tracking-[0.35em] text-primary text-xs md:text-sm font-semibold mb-4">Our Services</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4 capitalize">
              Property Management
            </h1>
            <p className="uppercase tracking-[0.35em] text-white text-xs md:text-sm font-semibold">
              Professional Property Management Solutions
            </p>
          </div>
        </div>
      </ImageHero>

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Services Overview */}
          <section className="mb-8">
            <p className="uppercase tracking-[0.3em] text-obsidian text-sm font-semibold text-center mb-3">What We Offer</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-center mb-6 text-obsidian capitalize">Our Management Services</h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-2 text-obsidian capitalize">Property Maintenance</h3>
                <p className="text-charcoal leading-relaxed">Comprehensive maintenance and repairs for your property.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-2 text-obsidian capitalize">Tenant Management</h3>
                <p className="text-charcoal leading-relaxed">Professional tenant screening and relationship management.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-2 text-obsidian capitalize">Security Services</h3>
                <p className="text-charcoal leading-relaxed">24/7 security monitoring and access control systems.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-2 text-obsidian capitalize">Facility Management</h3>
                <p className="text-charcoal leading-relaxed">Complete facility management and optimization services.</p>
              </div>
            </div>
          </section>

          {/* Properties Section */}
          <section className="mb-8">
            <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-obsidian capitalize">Our Portfolio</h2>
            {loading ? (
              <Spinner label="Loading properties..." />
            ) : properties.length === 0 ? (
              <div className="text-center py-12 text-taupe">No properties currently under management.</div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                {properties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            )}
          </section>

          {/* Benefits Section */}
          <section className="bg-obsidian p-8 rounded-lg border-t-4 border-primary">
            <p className="uppercase tracking-[0.3em] text-primary text-sm font-semibold mb-3">Why Us</p>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6 text-white capitalize">Why Choose Our Management Services?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-charcoal p-6 rounded-lg border-t-4 border-primary">
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white capitalize">Professional Management</h3>
                <p className="text-ash-light leading-relaxed">Expert property management services with proven track record.</p>
              </div>
              <div className="bg-charcoal p-6 rounded-lg border-t-4 border-primary">
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white capitalize">Transparent Reporting</h3>
                <p className="text-ash-light leading-relaxed">Regular detailed reports on property performance and maintenance.</p>
              </div>
              <div className="bg-charcoal p-6 rounded-lg border-t-4 border-primary">
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white capitalize">24/7 Support</h3>
                <p className="text-ash-light leading-relaxed">Round-the-clock assistance for property-related issues.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
